import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  Clock,
  CheckCircle,
  Eye,
  Filter,
  Car,
  Home,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGetUserMarketplaceInquiriesQuery } from '../../store/slices/apiSlice';
import Navbar from '../../components/common/Navbar';

const MarketplaceInquiries: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'in_progress' | 'resolved'>('all');
  const [selectedInquiry, setSelectedInquiry] = useState<any | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { data: inquiriesResponse, isLoading } = useGetUserMarketplaceInquiriesQuery({
    page: 1,
    limit: 50,
    status: statusFilter === 'all' ? undefined : statusFilter,
  }, {
    skip: !user,
  });

  const inquiries = inquiriesResponse?.data?.inquiries || [];

  const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'resolved':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Resolved
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            In Progress
          </Badge>
        );
      case 'new':
        return (
          <Badge variant="info" className="flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            New
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredInquiries = inquiries;

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Inquiries</h1>
            <p className="text-gray-600">View your marketplace inquiries and responses</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
            {(['all', 'new', 'in_progress', 'resolved'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'in_progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </Card>

        {/* Inquiries List */}
        {filteredInquiries.length === 0 ? (
          <Card className="p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Inquiries Found</h3>
            <p className="text-gray-600 mb-6">
              {statusFilter === 'all'
                ? "You haven't made any inquiries yet."
                : `No ${statusFilter} inquiries found.`}
            </p>
            <Button onClick={() => navigate('/marketplace')}>Browse Marketplace</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredInquiries.map((inquiry: any) => (
              <Card key={inquiry.inquiry_id} className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {inquiry.listing?.title || 'N/A'}
                        </h3>
                        {inquiry.listing && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            {inquiry.listing.listing_type === 'car' ? (
                              <Car className="w-4 h-4" />
                            ) : (
                              <Home className="w-4 h-4" />
                            )}
                            <span>{inquiry.listing.listing_type === 'car' ? 'Car' : 'House'}</span>
                          </div>
                        )}
                      </div>
                      {getStatusBadge(inquiry.status)}
                    </div>
                    <div className="mt-4">
                      <label className="text-xs font-medium text-gray-600">Your Message</label>
                      <p className="text-sm text-gray-900 mt-1 line-clamp-2">
                        {inquiry.message}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Date</label>
                        <p className="text-sm text-gray-900">
                          {formatDate(inquiry.created_at)}
                        </p>
                      </div>
                      {inquiry.contact_email && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Contact Email</label>
                          <p className="text-sm text-gray-900">
                            {inquiry.contact_email}
                          </p>
                        </div>
                      )}
                      {inquiry.contact_phone && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Contact Phone</label>
                          <p className="text-sm text-gray-900">
                            {inquiry.contact_phone}
                          </p>
                        </div>
                      )}
                    </div>
                    {inquiry.admin_response && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <label className="text-xs font-medium text-gray-600 mb-2 block">Admin Response</label>
                        <p className="text-sm text-gray-900">
                          {inquiry.admin_response}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedInquiry(inquiry);
                        setShowDetailModal(true);
                      }}
                      className="flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedInquiry(null);
        }}
        title="Inquiry Details"
      >
        {selectedInquiry && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Listing</label>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {selectedInquiry.listing?.title || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <div className="mt-1">
                {getStatusBadge(selectedInquiry.status)}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Your Message</label>
              <p className="text-sm text-gray-900 mt-1 p-3 bg-gray-50 rounded-lg">
                {selectedInquiry.message}
              </p>
            </div>
            {selectedInquiry.contact_email && (
              <div>
                <label className="text-sm font-medium text-gray-600">Contact Email</label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedInquiry.contact_email}
                </p>
              </div>
            )}
            {selectedInquiry.contact_phone && (
              <div>
                <label className="text-sm font-medium text-gray-600">Contact Phone</label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedInquiry.contact_phone}
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-600">Submitted On</label>
              <p className="text-sm text-gray-900 mt-1">
                {formatDate(selectedInquiry.created_at)}
              </p>
            </div>
            {selectedInquiry.admin_response && (
              <div>
                <label className="text-sm font-medium text-gray-600">Admin Response</label>
                <p className="text-sm text-gray-900 mt-1 p-3 bg-blue-50 rounded-lg">
                  {selectedInquiry.admin_response}
                </p>
              </div>
            )}
            {!selectedInquiry.admin_response && (
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  Your inquiry is being reviewed. You will receive a response soon.
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
      </div>
    </>
  );
};

export default MarketplaceInquiries;
